import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Home, Target, Tag, Map, Repeat } from "lucide-react";

const navItems = [
  { path: "/dashboard", label: "Home", icon: Home },
  { path: "/goals", label: "Goals", icon: Target, badgeKey: "goals" },
  { path: "/deals", label: "Deals", icon: Tag },
  { path: "/marketplace", label: "Market", icon: Repeat, badgeKey: "market" },
  { path: "/community", label: "Community", icon: Map },
];

const API = import.meta.env.VITE_API_URL || "http://localhost:8000";

function Badge({ count }) {
  if (!count) return null;
  return (
    <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none">
      {count > 99 ? "99+" : count}
    </span>
  );
}

export default function BottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const [badges, setBadges] = useState({ goals: 0, market: 0 });

  const hiddenPaths = ["/", "/onboarding"];
  const isHidden = hiddenPaths.includes(location.pathname) || location.hash?.includes("session_id");

  useEffect(() => {
    if (isHidden) return;
    const token = localStorage.getItem("token");
    if (!token) return;

    const headers = { Authorization: `Bearer ${token}` };

    async function fetchBadges() {
      try {
        const [invRes, msgRes] = await Promise.allSettled([
          fetch(`${API}/goals/invitations`, { headers }),
          fetch(`${API}/marketplace/messages/unread-count`, { headers }),
        ]);

        let goalsCount = 0;
        if (invRes.status === "fulfilled" && invRes.value.ok) {
          const data = await invRes.value.json();
          goalsCount = Array.isArray(data) ? data.length : (data.count ?? 0);
        }

        let marketCount = 0;
        if (msgRes.status === "fulfilled" && msgRes.value.ok) {
          const data = await msgRes.value.json();
          marketCount = data.count ?? 0;
        }

        setBadges({ goals: goalsCount, market: marketCount });
      } catch {
        // silently fail — badges are non-critical
      }
    }

    fetchBadges();
    const interval = setInterval(fetchBadges, 60_000);
    return () => clearInterval(interval);
  }, [isHidden, location.pathname]);

  if (isHidden) return null;

  return (
    <nav className="bottom-nav" data-testid="bottom-navigation">
      {navItems.map((item) => {
        const isActive = location.pathname === item.path;
        const Icon = item.icon;
        const badgeCount = item.badgeKey ? badges[item.badgeKey] : 0;

        return (
          <button
            key={item.path}
            onClick={() => navigate(item.path)}
            className={`nav-item ${isActive ? "active" : ""}`}
            data-testid={`nav-${item.label.toLowerCase()}`}
          >
            <span className="relative inline-flex">
              <Icon
                className="nav-icon"
                strokeWidth={isActive ? 2.5 : 2}
                fill={isActive ? "currentColor" : "none"}
              />
              <Badge count={badgeCount} />
            </span>
            <span className="nav-label">{item.label}</span>
          </button>
        );
      })}
    </nav>
  );
}
